/**
 * Domain enumerations.
 *
 * These are declared as `as const` tuples so the same list can drive the zod
 * schemas, the TypeScript unions and the Prisma enums without drifting. When a
 * value is added here it must also be added to `apps/api/prisma/schema.prisma`
 * and a migration generated.
 */
export const USER_ROLES = ['ADMIN', 'CREATOR'];
export const FORM_TYPES = ['AGREEMENT', 'ACKNOWLEDGEMENT', 'CONSENT', 'FORM', 'OTHER'];
export const FORM_STATUSES = ['DRAFT', 'PUBLISHED', 'CLOSED', 'ARCHIVED'];
export const ACCESS_TYPES = ['PRIVATE', 'PUBLIC_LINK'];
/** How the content of a form version is authored. */
export const CONTENT_MODES = ['NATIVE', 'PDF'];
export const FIELD_TYPES = [
    // Static content — renders, never collects a value.
    'HEADING',
    'PARAGRAPH',
    'DIVIDER',
    'INFO',
    // Inputs.
    'SHORT_TEXT',
    'LONG_TEXT',
    'EMAIL',
    'NUMBER',
    'DATE',
    'SELECT',
    'RADIO',
    'CHECKBOX',
    'MULTI_CHECKBOX',
    // Identity helpers — prefilled from the respondent, mostly used on PDFs.
    'FULL_NAME',
    'INITIALS',
    // Legal acceptance.
    'ACKNOWLEDGEMENT',
    'AGREEMENT',
    // Signature.
    'SIGNATURE',
];
/**
 * `PROCESSING` is an internal state that is not part of the conceptual model:
 * it exists so that completing a submission is idempotent. A submission enters
 * PROCESSING under a conditional update, so a double click can never generate
 * two documents. See docs/architecture.md#submission-completion.
 */
export const SUBMISSION_STATUSES = [
    'STARTED',
    'PROCESSING',
    'COMPLETED',
    'CANCELLED',
    'INVALIDATED',
];
/**
 * What the creator's list of responses shows, which is not quite what the
 * database stores.
 *
 * `IDENTIFIED_ONLY` is derived, never stored: a submission that is STARTED and
 * holds no answer at all. The person typed their name and address and left,
 * which reads very differently from an answer that is halfway written - and
 * calling both of them "En proceso" is what once made a signed agreement look
 * unsigned. See `SubmissionSummaryDto.displayStatus`.
 */
export const SUBMISSION_DISPLAY_STATUSES = [
    'IDENTIFIED_ONLY',
    'STARTED',
    'PROCESSING',
    'COMPLETED',
    'CANCELLED',
    'INVALIDATED',
];
/**
 * What each of those statuses is called wherever somebody reads one.
 *
 * Here rather than in the web app because the CSV export says it too, and an
 * export whose words differ from the screen's is a second version of the same
 * fact - which is how a row saying "STARTED" next to a signed agreement came
 * to be read as unsigned.
 */
export const SUBMISSION_DISPLAY_STATUS_LABELS = {
    IDENTIFIED_ONLY: 'Solo se identificó',
    STARTED: 'En proceso',
    PROCESSING: 'Finalizando',
    COMPLETED: 'Firmado',
    CANCELLED: 'Cancelado',
    INVALIDATED: 'Anulado',
};
export const SUBMISSION_EVENT_TYPES = [
    'FORM_OPENED',
    'SUBMISSION_CREATED',
    'SUBMISSION_RESUMED',
    'EMAIL_VERIFIED',
    'DOCUMENT_OPENED',
    'FIELD_UPDATED',
    'AGREEMENT_ACCEPTED',
    'SIGNATURE_ADDED',
    'SUBMISSION_COMPLETED',
    'DOCUMENT_GENERATED',
    'SUBMISSION_CANCELLED',
    'SUBMISSION_INVALIDATED',
];
export const DOCUMENT_KINDS = ['SOURCE_PDF', 'SIGNED_PDF', 'SIGNATURE_IMAGE'];
/** How the respondent of a submission was identified. */
export const AUTH_METHODS = [
    'PUBLIC_SELF_DECLARED',
    'EMAIL_VERIFIED',
    'ENTRA_ID',
    'LOCAL',
];
/** What a public access token allows its holder to do. */
export const ACCESS_TOKEN_SCOPES = ['SUBMISSION_FILL', 'SUBMISSION_VIEW'];
//# sourceMappingURL=enums.js.map